import React, { useEffect, useState } from "react";
import { Modal } from "components/ui/Modal";
import { getItem } from "api/items";
import { Button } from "components/ui/Button";

export function ProductDetailsModal({ open, itemId, onClose }) {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && itemId) {
            setLoading(true);
            setError(null);
            getItem(itemId)
                .then((data) => {
                    setItem(data);
                })
                .catch((err) => {
                    setError(err.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setItem(null);
        }
    }, [open, itemId]);

    if (!open) return null;

    const fmtMoney = (v) =>
        new Intl.NumberFormat("ru-RU", {
            style: "currency",
            currency: "UZS",
            maximumFractionDigits: 0,
        }).format(v || 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Mahsulot kartasi"
            className="max-w-2xl bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
        >
            <div className="p-6 space-y-6">
                {loading && (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg border border-red-100 dark:border-red-900/50">
                        Xatolik: {error}
                    </div>
                )}

                {item && !loading && (
                    <div className="space-y-6">
                        {/* Заголовок с основной инфой */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{item.name}</h2>
                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-slate-400 font-mono">
                                    <span>SKU: {item.sku || "—"}</span>
                                    <span className="text-gray-300 dark:text-slate-600">|</span>
                                    <span>ID: {item.id}</span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {item.is_consumable && (
                                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800">
                                        Sarf materiali
                                    </div>
                                )}
                                <div
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${item.is_archived
                                        ? "bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200"
                                        : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                                        }`}
                                >
                                    {item.is_archived ? "Arxivda" : "Faol"}
                                </div>
                            </div>
                        </div>

                        {/* Сетка основных параметров */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700">
                            <div className="space-y-1">
                                <span className="text-sm text-gray-500 dark:text-slate-400">Kategoriya</span>
                                <p className="font-medium text-gray-900 dark:text-slate-100">
                                    {item.category_name || "Ko'rsatilmagan"}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm text-gray-500 dark:text-slate-400">Yetkazib beruvchi</span>
                                <p className="font-medium text-gray-900 dark:text-slate-100">
                                    {item.supplier_name || "Ko'rsatilmagan"}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm text-gray-500 dark:text-slate-400">Birlik narxi (oxirgi)</span>
                                <p className="font-medium text-gray-900 dark:text-slate-100">
                                    {fmtMoney(item.last_price)}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm text-gray-500 dark:text-slate-400">Qoldiqning umumiy qiymati</span>
                                <p className="font-medium text-gray-900 dark:text-slate-100">
                                    {fmtMoney((item.qty_on_hand || 0) * (item.last_price || 0))}
                                </p>
                            </div>
                        </div>

                        {/* Блок остатков */}
                        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100 mb-4">
                                Ombor qoldiqlari
                            </h3>
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                                        {item.qty_on_hand}
                                    </span>
                                    <span className="text-gray-500 dark:text-slate-400 ml-2 text-lg">
                                        {item.unit || "шт"}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">Minimal qoldiq</p>
                                    <p className="font-semibold text-gray-900 dark:text-slate-100">
                                        {item.min_stock} {item.unit}
                                    </p>
                                    {item.qty_on_hand < item.min_stock && (
                                        <p className="text-red-500 dark:text-red-400 text-sm font-medium mt-1">
                                            Yetishmovchilik: {(item.min_stock - item.qty_on_hand).toFixed(2)}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {/* Прогресс бар (визуально) */}
                            <div className="mt-4 h-2 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${item.qty_on_hand < item.min_stock
                                        ? "bg-red-500 dark:bg-red-500"
                                        : "bg-blue-500 dark:bg-blue-500"
                                        }`}
                                    style={{
                                        width: `${Math.min(
                                            100,
                                            (item.qty_on_hand / (Math.max(item.qty_on_hand, item.min_stock * 2) || 1)) * 100
                                        )}%`,
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-slate-700">
                            <Button onClick={onClose} variant="secondary" className="bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-slate-100 hover:bg-gray-200 dark:hover:bg-slate-700">
                                Yopish
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
